import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Target } from "lucide-react";
import ToolPageLayout from "@/components/tools/ToolPageLayout";
import { getRelatedTools } from "@/lib/toolsData";

const CPACalculator = () => {
  const [totalCost, setTotalCost] = useState<string>("");
  const [conversions, setConversions] = useState<string>("");
  const [targetCPA, setTargetCPA] = useState<string>("");
  const [result, setResult] = useState<{ cpa: number; status: string } | null>(null);

  const calculateCPA = () => {
    const costNum = parseFloat(totalCost) || 0;
    const conversionsNum = parseFloat(conversions) || 0;
    const targetNum = parseFloat(targetCPA) || 0;
    
    if (conversionsNum === 0) {
      setResult(null);
      return;
    }

    const cpa = costNum / conversionsNum;
    let status = "On Track";
    
    if (targetNum > 0) {
      if (cpa <= targetNum * 0.8) status = "Under Budget";
      else if (cpa <= targetNum) status = "On Track";
      else if (cpa <= targetNum * 1.2) status = "Slightly Over";
      else status = "Over Budget";
    }
    
    setResult({ cpa, status });
  };

  const reset = () => {
    setTotalCost("");
    setConversions("");
    setTargetCPA("");
    setResult(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Under Budget": return "text-green-600";
      case "On Track": return "text-green-500";
      case "Slightly Over": return "text-yellow-600";
      case "Over Budget": return "text-red-600";
      default: return "text-foreground";
    }
  };

  const relatedTools = getRelatedTools("cpa-calculator");

  return (
    <ToolPageLayout
      title="CPA Calculator"
      description="Calculate your Cost Per Acquisition to understand how much you spend to acquire each new customer."
      metaDescription="CPA (Cost Per Acquisition) measures the average cost to acquire a customer. It's essential for understanding marketing efficiency, setting budgets, and ensuring profitable customer acquisition."
      icon={Target}
      slug="cpa-calculator"
      formula={{
        equation: "CPA = Total Marketing Cost / Number of Acquisitions",
        variables: [
          { name: "Total Marketing Cost", description: "All costs associated with the campaign or channel" },
          { name: "Acquisitions", description: "Number of customers, leads, or conversions acquired" },
        ]
      }}
      example={{
        inputs: [
          { label: "Total Campaign Cost", value: "$5,000" },
          { label: "Conversions", value: "100" },
        ],
        output: "$50.00 CPA",
        interpretation: "Each new customer costs $50 to acquire. Compare this to Customer Lifetime Value to assess profitability."
      }}
      improvements={[
        "Improve targeting to reach higher-quality prospects",
        "Optimize conversion rates on landing pages",
        "Test different ad creatives and messaging",
        "Focus budget on highest-performing channels",
        "Implement retargeting to convert warm audiences",
        "Reduce friction in the conversion funnel",
      ]}
      relatedTools={relatedTools.map(t => ({ title: t.title, href: t.href, description: t.description }))}
      relatedService={{ title: "CRO Services", href: "/cro" }}
    >
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Calculate CPA</CardTitle>
          <CardDescription>Enter your costs and conversions</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="totalCost">Total Campaign Cost ($)</Label>
            <Input
              id="totalCost"
              type="number"
              placeholder="5000"
              value={totalCost}
              onChange={(e) => setTotalCost(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="conversions">Total Conversions</Label>
            <Input
              id="conversions"
              type="number"
              placeholder="100"
              value={conversions}
              onChange={(e) => setConversions(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="targetCPA">Target CPA (optional)</Label>
            <Input
              id="targetCPA"
              type="number"
              placeholder="50"
              value={targetCPA}
              onChange={(e) => setTargetCPA(e.target.value)}
            />
          </div>

          <div className="flex gap-3">
            <Button onClick={calculateCPA} className="flex-1">
              Calculate CPA
            </Button>
            <Button variant="outline" onClick={reset}>
              Reset
            </Button>
          </div>

          {result && (
            <div className="mt-6 p-6 bg-primary/5 rounded-lg border border-primary/20">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Your CPA</p>
                <p className="text-4xl font-bold text-primary">
                  ${result.cpa.toFixed(2)}
                </p>
                {targetCPA && (
                  <p className={`text-sm mt-3 ${getStatusColor(result.status)}`}>
                    {result.status}
                  </p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </ToolPageLayout>
  );
};

export default CPACalculator;
